import React from "react";

import cx from "classnames";

import { BRANDING_CLASSES } from "../../styles/branding";
import { Button } from "../Button";
import { Callout } from "../Callout";
import { Card } from "../Card";
import { Divider } from "../Divider";
import { Flex } from "../Flex";
import { Spacer } from "../Grid/Spacer";
import Header from "../Header";
import { Heading } from "../Heading";
import { Link } from "../Link";
import { Text } from "../Text";

import styles from "./PointBalance.scss";

export interface PointBalanceProps {
  points?: PointBalancePoints;
  /**
   * If provided, we show this link at the top left, with the text equal to "text.rewardsHistory"
   */
  historyUrl?: string;
  /**
   * Various texts to use in places where we need to internationalize.
   */
  text: PointBalanceText;
  /**
   * Shows the "tokens" section, under the point balances.
   */
  showTokensSection?: boolean;
  /**
   * If provided, we show these token balances at the bottom of the card.
   */
  tokenBalances?: TokenBalanceProps[];
  /**
   * Removes the card styling if true.
   */
  removeCardStyling?: boolean;
  /**
   * If provided, we show this content in a notification <Callout> under the
   * point balances.
   */
  pointBalanceCallout?: string;
}

interface TokenBalanceProps {
  id: string | number;
  amount: string;
  description: string;
  url?: string;
  shopButtonText?: string;
}

interface PointBalanceText {
  title: string;
  sendPointsBefore?: string;
  earned: string;
  toSend: string;
  rewardsHistory: React.ReactNode;
}

interface PointBalancePoints {
  expirationDate?: string;
  sendPointsBefore?: string;
  givingBalance: number;
  redemptionBalance: number;
}

/**
 * This component assumes a point object will be passed in containing the necessary values.
 */
export function PointBalance({
  points = {
    givingBalance: 0,
    redemptionBalance: 0,
  },
  text,
  historyUrl,
  showTokensSection = false,
  tokenBalances = [],
  removeCardStyling = false,
  pointBalanceCallout,
}: PointBalanceProps) {
  const pointBalanceStyles = removeCardStyling ? "withoutCard" : "pointBalance";

  return (
    <Card className={styles[pointBalanceStyles]} data-testid="point-balance">
      <div className={styles.pointBalance__header}>
        <div className={styles.headerWrapper}>
          <div className={styles.headerWrapper__content}>
            <Heading className={styles.headerWrapper__title} level="3">
              {text.title}
            </Heading>
            {historyUrl && (
              <Link
                to={historyUrl}
                data-testid="view-all-past-sync-ups"
                target="_blank"
              >
                {text.rewardsHistory}
              </Link>
            )}
          </div>
          {points.expirationDate && (
            <Text
              data-testid="point-expiration"
              className={styles.pointBalance__sectionSubtitle}
            >
              {text.sendPointsBefore}
            </Text>
          )}
        </div>
      </div>
      <Divider />
      <Spacer />
      <Flex justifyContent="space-between" className={styles["textBreakpoint"]}>
        <div className={styles.pointBalance__toSend}>
          <Text className={styles.pointBalance__sectionHeader}>
            {text.toSend}
          </Text>
          <Header
            level="2"
            size="h2"
            data-testid="giving-balance"
            className={styles["balance"]}
          >
            {points.givingBalance.toLocaleString()}
          </Header>
        </div>
        <div
          className={cx([
            styles.pointBalance__rewards,
            BRANDING_CLASSES.ALT_TEXT_BORDER,
          ])}
        >
          <Text className={styles.pointBalance__sectionHeader}>
            {text.earned}
          </Text>
          <Header
            level="2"
            size="h2"
            data-testid="earned-balance"
            className={styles["balance"]}
          >
            {points.redemptionBalance.toLocaleString()}
          </Header>
        </div>
      </Flex>
      {pointBalanceCallout && (
        <Callout text={pointBalanceCallout} variant="information" />
      )}
      {showTokensSection && (
        <>
          <Spacer size="tiny" />
          <Divider />
          {tokenBalances.map((token) => (
            <TokenBalance key={token.id} {...token} />
          ))}
        </>
      )}
    </Card>
  );
}

function TokenBalance({
  amount,
  description,
  url,
  shopButtonText,
}: TokenBalanceProps) {
  return (
    <Flex
      className={styles.tokenItemContainer}
      data-testid="token-balance-container"
      alignItems="center"
      gap="16px"
    >
      <Header
        level="2"
        size="h2"
        data-testid="token-balance-amount"
        className={styles.tokenBalanceAmount}
      >
        {amount?.toLocaleString()}
      </Header>
      <Flex
        direction="column"
        alignItems="flex-start"
        justifyContent="center"
        className={styles.pointBalance__tokenTextFlex}
      >
        <Text
          className={cx(
            styles.pointBalance__sectionSubtitle,
            styles.tokenBalanceDescription
          )}
          data-testid="token-balance-description"
        >
          {description}
        </Text>
        {shopButtonText && url && (
          <Button
            className={styles.historyButton}
            href={url}
            variant="system"
            data-testid="token-balance-shop-button"
          >
            {shopButtonText}
          </Button>
        )}
      </Flex>
    </Flex>
  );
}
